import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthModal from "../components/AuthModal";
import SubscribeSheet from "../components/SubscribeSheet";
import CosmicBackground from "../components/CosmicBackground";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { savePendingGuestQuestion } from "../hooks/useGuestChat";
import { useCareerMotion } from "./useCareerMotion";
import "./CareerPage.css";

/**
 * /career, career astrology landing page.
 *
 * The markup and styling are the approved design (aura-career-india.html),
 * ported rather than reinterpreted. The stylesheet is the original one with
 * every selector scoped under .crr-root; see CareerPage.css for why that
 * matters.
 *
 * INDEXED. The career cluster already earns ~145 clicks and 1,418 impressions
 * a quarter with no page behind it ("career astrology free ai", "career
 * prediction by date of birth ai", "career horoscope ai"), all landing on the
 * homepage at 9-22% CTR against a 6.5% site average. The copy is Hinglish and
 * converts; the search terms live in the title, the description and the FAQ
 * rather than being forced into the hero.
 *
 * RETARGET 2026-10-13 (item 21): GSC 28d, the cluster still lives on the
 * HOMEPAGE ("career astrology free ai" 30 clicks / 156 imp @3.0, 1-of-1
 * homepage; "career astrology ai" 3 / 52 @4.4; "ai career astrology" 6 / 49
 * @3.6) while this page sat at 0 clicks / 3 imp @22.3, the weakest page on
 * the site. Title/og/description/sub now LEAD with the AI-suffixed head
 * "Career Astrology AI" the cluster actually uses, and the sitewide footer
 * anchor flips to match. H1 stays on the decision, per the note above.
 *
 * NOT ported, deliberately:
 *   - the reviewer strip and the A/B/C H1 switcher. Dev harness.
 *   - the page's own <header> and <footer>. The app already renders both.
 *   - pricing. The design carries "₹ price" placeholders on both paid tiers.
 *   - the three review slots. They are PLACEHOLDER text, and invented reviews
 *     are not an option.
 *   - the ask <dialog>. Its question -> birth details -> OTP flow is the
 *     guest-chat flow we already ship and have already debugged. Every CTA
 *     parks the question and hands off to /chat, so the app keeps one auth
 *     path rather than two.
 *
 * Interactivity is delegated from the root instead of being wired onto each
 * button, which keeps the ported markup byte-identical to the design. The tab
 * panels are toggled imperatively, exactly as the original script did: nothing
 * in this component re-renders, so React never reconciles those nodes away.
 */

/* Chip key -> the question it asks. Keys are the design's own data-q values,
   which also back the ?q= deep link so an ad can land on a pre-chosen
   question. */
const QUESTIONS: Record<string, string> = {
  switch: "Job switch karun ya ruk jaun?",
  "sarkari-yog": "Sarkari naukri ka yog hai kya?",
  taiyari: "Taiyari jaari rakhun ya chhod doon?",
  business: "Business shuru karun ya nahi?",
  papa: "Papa ka business join karun?",
  "private-safe": "Private job mere liye sahi hai?",
  partnership: "Partnership mein business karun?",
};

const FAQ_SCHEMA: [string, string][] = [
  [
    "Kya ye batayega mera exam clear hoga?",
    "Nahi. Exam result tumhari taiyari, cut-off aur exam process pe depend karta hai. Aura batata hai ki tumhare chart mein service ke indications kitne strong hain, aur kaunsa phase pura zor lagane ke liye traditionally favourable hai.",
  ],
  [
    "Business mein profit hoga ya nahi, ye batayega?",
    "Nahi. Koi bhi paise ya profit ki guarantee nahi de sakta. Aura dikhata hai ki tumhara chart business ya partnership ki taraf kitna indicate karta hai, aur kaunsa phase shuru karne ke liye better hai.",
  ],
  [
    "Kya ye sach mein accurate hai?",
    "Kundli Swiss Ephemeris astronomy data se banti hai, aur Aura har insight ke saath batata hai ki wo kis house, planet aur period se aayi. Ye phases aur favourable windows dikhata hai, fixed dates nahi.",
  ],
  [
    "Birth time nahi pata?",
    "Chalega. Approximate time se bhi chart ban jaata hai aur zyada tar cheezein padhi ja sakti hain. Exact time hone se lagna aur dasha timing sharper hoti hai, isliye jitna paas ho sake utna accurate time daalo.",
  ],
  [
    "Free sawaal ke baad kya?",
    "Tumhe poora jawaab milta hai. Agar sarkari/private/business ki poori picture, favourable windows aur D10 strengths chahiye, toh Career Path Report mein milega.",
  ],
  [
    "What is career astrology?",
    "Career astrology reads the part of your birth chart that describes work: the 10th house and its lord, the D10 or Dashamsha divisional chart, the 6th house for service and competition, and the 3rd and 7th for your own ventures. Read together with the dasha period you are running, it describes the kind of work your chart leans toward and which phase you are in, rather than naming a job title.",
  ],
  [
    "Can astrology predict my career by date of birth?",
    "A career prediction by date of birth needs the date, the time and the place, not the date alone. Those three fix the lagna, the house cusps and the dasha sequence, which is where the career reading comes from. With only a date you get the planetary signs but not the houses, and the houses are what make it a career reading.",
  ],
  [
    "App download karna padega?",
    "Nahi. Yahin browser mein chalta hai. Android app bhi hai.",
  ],
];

export default function CareerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  useCareerMotion(rootRef);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingReport, setPendingReport] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  const {
    user,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    pricingVariant,
  } = useAuth();

  /* Live price for the Career & Success Blueprint, the same figure /reports
     prints on its unlock button. Read rather than hardcoded so it follows the
     visitor's currency and any member rate. */
  const { prices, formatPrice } = usePricing();
  const reportPrice = prices["report_premium"] ?? 0;

  /* Same auth wiring as HomePage and GuestChatPage, so the login sheet behaves
     identically everywhere. */
  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp],
  );
  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );
  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => verifyMsg91Otp({ ...payload, variant: pricingVariant }),
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  /* Park the question, then route by auth state.
     A guest gets the login sheet HERE, not a trip to /chat. ChatRoute sends a
     logged-out INDIAN visitor to the homepage, because guest chat is a
     non-Indian teaser, and this page is Hinglish and written for exactly that
     visitor: sarkari naukri, ghar wale, RAS ki taiyari. Navigating them to
     /chat threw away the click and the parked question with it. */
  const ask = (question?: string) => {
    if (question) savePendingGuestQuestion(question);
    if (user) {
      navigate("/chat");
      return;
    }
    setAuthOpen(true);
  };

  /* "Kya milega, dekho" opens the Career & Success Blueprint, the same premium
     report ReportUpsellCard links to. It pointed at href="#plans" in the
     design, and we ship no pricing section, so it was an anchor to nothing.

     /reports is behind ProtectedRoute, so a guest signs in first and the effect
     below carries them the rest of the way. Same shape as the compatibility
     flow, which hands /compatibility-report its state on arrival. */
  const openCareerReport = () => {
    if (user) {
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
      return;
    }
    setPendingReport(true);
    setAuthOpen(true);
  };

  /* Membership is the app's own SubscribeSheet, the same one the navbar opens,
     so the offer, the arm (autopay vs one-time) and the price all come from
     one place. It needs a signed-in user to charge, so a guest signs in first. */
  const openMembership = () => {
    if (user) {
      setSubscribeOpen(true);
      return;
    }
    setPendingSubscribe(true);
    setAuthOpen(true);
  };

  /* A guest who asked for the report gets taken there once they are in, rather
     than being left on the landing page wondering what the click did. */
  useEffect(() => {
    if (user && pendingReport) {
      setPendingReport(false);
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
    }
  }, [user, pendingReport, navigate]);

  /* Same for membership: open the sheet once they are in. */
  useEffect(() => {
    if (user && pendingSubscribe) {
      setPendingSubscribe(false);
      setSubscribeOpen(true);
    }
  }, [user, pendingSubscribe]);

  const showPath = (key: string) => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>(".tab").forEach((t) => {
      const on = t.dataset.path === key;
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector<HTMLElement>("#p-" + t.dataset.path);
      if (panel) panel.hidden = !on;
    });
  };

  /* ?q= preselects a question, ?path= preselects a tab. Both are the design's
     own parameters, kept so a campaign can point at one variant. */
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const path = p.get("path");
    if (path && rootRef.current?.querySelector("#p-" + path)) showPath(path);
    const q = p.get("q");
    if (q && QUESTIONS[q]) {
      rootRef.current
        ?.querySelectorAll<HTMLElement>(".chip")
        .forEach((c) => c.setAttribute("aria-pressed", String(c.dataset.q === q)));
    }
  }, [location.search]);

  /* One delegated handler, so the ported markup needs no per-button props. */
  const onClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    const tab = el.closest<HTMLElement>(".tab");
    if (tab?.dataset.path) {
      showPath(tab.dataset.path);
      return;
    }
    const chip = el.closest<HTMLElement>(".chip");
    if (chip?.dataset.q) {
      ask(QUESTIONS[chip.dataset.q]);
      return;
    }
    if (el.closest("[data-report]")) {
      openCareerReport();
      return;
    }
    if (el.closest("[data-subscribe]")) {
      openMembership();
      return;
    }
    const cta = el.closest<HTMLElement>("[data-ask]");
    if (cta) {
      ask(cta.dataset.preset ? QUESTIONS[cta.dataset.preset] : undefined);
    }
  };

  /* Arrow-key tab navigation, matching the design's keyboard behaviour. */
  const onKeyDown = (e: React.KeyboardEvent) => {
    const tab = (e.target as HTMLElement).closest<HTMLElement>(".tab");
    if (!tab || !rootRef.current) return;
    const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const tabs = Array.from(rootRef.current.querySelectorAll<HTMLElement>(".tab"));
    const i = tabs.indexOf(tab);
    const next = tabs[(i + d + tabs.length) % tabs.length];
    if (next?.dataset.path) {
      showPath(next.dataset.path);
      next.focus();
    }
  };

  return (
    <div className="crr-root" ref={rootRef} onClick={onClick} onKeyDown={onKeyDown}>
      <CosmicBackground />
      <Helmet>
        <title>Career Astrology AI, Free: Switch Jobs or Wait? | Aura AI</title>
        <meta
          name="description"
          content="Free career astrology AI. Switch jobs, wait for sarkari, or start a business? Aura AI reads your 10th house, D10 and dashas for a prediction by date of birth."
        />
        <link rel="canonical" href="https://astroaura.ai/career/" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@400;500&display=swap"
        />
        <meta property="og:title" content="Career Astrology AI, Free: Prediction by Date of Birth | Aura AI" />
        <meta
          property="og:description"
          content="Switch, wait or take the risk? Ask Aura AI using your exact birth chart. First question free."
        />
        <meta property="og:url" content="https://astroaura.ai/career/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_SCHEMA.map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Aura AI", item: "https://astroaura.ai/" },
              { "@type": "ListItem", position: 2, name: "Career Astrology AI", item: "https://astroaura.ai/career/" },
            ],
          })}
        </script>
      </Helmet>

      <main>
        <section className="hero" aria-labelledby="h1">
          <div className="wrap">
            <div>
              <div className="trust"><span><b>★ 4.4</b> on Google Play</span><span><b>1M+</b> downloads</span><span>Private &amp; encrypted</span></div>
              <h1 id="h1" data-variant="a">Should You Switch Jobs, Wait <em>or Take the Risk?</em></h1>
              <p className="sub"><strong>Free career astrology AI, read from your exact birth chart.</strong> Private job, sarkari taiyari, ya apna business? Aura tumhare <strong>10th house, 6th house, D10 aur dashas</strong> ek saath padhta hai, aur batata hai abhi kaunsa phase chal raha hai.</p>
              <button className="btn" type="button" data-ask>Apna Sawaal Poochho, FREE <span aria-hidden="true">→</span></button>
              <div className="micro"><span>Pehla sawaal bilkul free</span><span>Koi card nahi</span><span>60 second</span></div>

              <p className="chips-label" id="chips-label">Ya inme se chuno</p>
              <div className="chips" role="group" aria-labelledby="chips-label">
                <button className="chip" type="button" data-q="switch">Job switch karun?</button>
                <button className="chip" type="button" data-q="sarkari-yog">Sarkari yog hai?</button>
                <button className="chip" type="button" data-q="taiyari">Taiyari jaari rakhun?</button>
                <button className="chip" type="button" data-q="business">Business shuru karun?</button>
                <button className="chip" type="button" data-q="private-safe">Private job sahi hai?</button>
                <button className="chip" type="button" data-q="papa">Papa ka business?</button>
              </div>
            </div>

            <aside className="reading" aria-label="Sample reading">
              <div className="reading-head"><span className="tag">Sample reading</span><span className="tag">Hinglish</span></div>
              <div className="kundli">
                <svg viewBox="0 0 300 300" role="img" aria-label="Sample North Indian kundli with the 10th and 6th houses highlighted">
                  <rect x="1" y="1" width="298" height="298" fill="#140718" stroke="#44264b" strokeWidth="2"/>
                  <polygon points="150,150 225,75 300,150 225,225" fill="rgba(255,204,253,.18)"/>
                  <polygon points="0,300 150,300 75,225" fill="rgba(242,207,138,.14)"/>
                  <g stroke="#44264b" strokeWidth="2" fill="none">
                    <line x1="0" y1="0" x2="300" y2="300"/><line x1="300" y1="0" x2="0" y2="300"/>
                    <polygon points="150,0 300,150 150,300 0,150"/>
                  </g>
                  <polygon points="150,150 225,75 300,150 225,225" fill="none" stroke="#ffccfd" strokeWidth="2.5"/>
                  <g fontFamily="IBM Plex Mono, monospace" fontSize="15" fill="#8d7593" textAnchor="middle">
                    <text x="150" y="60">1</text><text x="75" y="22">2</text><text x="22" y="80">3</text><text x="75" y="140">4</text>
                    <text x="22" y="230">5</text><text x="110" y="292" fill="#f2cf8a">6</text><text x="150" y="245">7</text><text x="225" y="292">8</text>
                    <text x="278" y="230">9</text><text x="240" y="140" fill="#ffccfd">10</text><text x="278" y="80">11</text><text x="225" y="22">12</text>
                  </g>
                  <g fontFamily="Sora, sans-serif" fontSize="17" fontWeight="700" textAnchor="middle">
                    <text x="215" y="172" fill="#ffccfd">Sa</text>
                    <text x="68" y="286" fill="#f2cf8a">Su</text>
                    <text x="75" y="170" fill="#f7eef8">Ju</text>
                    <text x="150" y="90" fill="#bfa9c4">Me</text>
                  </g>
                </svg>
                <dl>
                  <div><dt>10th lord</dt><dd><span className="hl">Sun</span> in the 6th</dd></div>
                  <div><dt>10th house</dt><dd>Saturn</dd></div>
                  <div><dt>Mahadasha</dt><dd>Saturn</dd></div>
                </dl>
              </div>
              <div className="msg user">3 saal se taiyari kar raha hoon. Ghar wale bol rahe private join kar lo. Kya karun?</div>
              <div className="msg aura"><span className="who">Aura AI</span>Tumhara 10th lord <strong>Sun 6th house</strong> mein hai. Traditionally ye service aur competitive exams ke liye strong indication maana jaata hai. 10th mein Saturn structured, rule-based kaam ki taraf jhukta hai. <strong>Saturn mahadasha</strong> slow par steady phase hai: agle 12-18 mahine preparation sharp karne ke liye favourable dikhte hain. Chart sarkari raaste ko support karta hai, par ek backup plan rakhna bhi samajhdaari hai.</div>
              <p className="reading-foot">Example for illustration · Chart shows indications &amp; phases, not exam results</p>
            </aside>
          </div>
        </section>

        


        


        <section className="rule" aria-labelledby="paths">
          <div className="wrap">
            <p className="eyebrow">Switch · Wait · Take the risk</p>
            <h2 id="paths">Teen raaste. Teeno mein darr. <em>Tumhara chart kis taraf hai?</em></h2>
            <p className="lede">Har raaste ke liye kundli mein alag cheezein dekhi jaati hain. Jo tumhari situation hai, wo chuno.</p>

            <div className="paths">
              <div className="tabs" role="tablist" aria-label="Career path">
                <button className="tab" role="tab" id="t-private" aria-controls="p-private" aria-selected="true" data-path="private"><b>Switch</b><span>Private job badlun?</span></button>
                <button className="tab" role="tab" id="t-sarkari" aria-controls="p-sarkari" aria-selected="false" data-path="sarkari" tabIndex="-1"><b>Wait</b><span>Sarkari taiyari · ruk jaun?</span></button>
                <button className="tab" role="tab" id="t-business" aria-controls="p-business" aria-selected="false" data-path="business" tabIndex="-1"><b>Take the risk</b><span>Apna business shuru karun?</span></button>
              </div>

              <div className="panel" role="tabpanel" id="p-private" aria-labelledby="t-private">
                <div>
                  <h3>Salary aati hai. Sukoon nahi.</h3>
                  <ul className="fears"><li>Har appraisal mein "next year pakka."</li><li>Layoff ki news, aur EMI har mahine.</li><li>Junior aage nikal gaya. Tum wahi ke wahi.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>10th house &amp; D10</b> · field aur growth ka pattern</li><li><b>Amatya Karaka</b> · tumhari working strengths</li><li><b>6th &amp; 11th houses</b> · competition, service, gains</li><li><b>Saturn &amp; Jupiter transits</b> · switch ya promotion ke windows</li></ul>
                </div>
                <div>
                  <div className="msg user">Offer aaya hai, par current job stable hai. Switch karun?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>Tumhara Amatya Karaka Mercury 10th mein strong hai, aur tum abhi <strong>Saturn-Mercury</strong> mein ho: traditionally ek build phase, jahan steady, skill-based moves leap se better fit hote hain. Chart us switch ki taraf jhukta hai jo tumhare current kaam pe build kare. Sirf hike nahi, role dekho.</div>
                  <div style={{marginTop: '18px'}}><button className="btn" type="button" data-ask data-preset="private-safe">Mera career phase dekho →</button></div>
                </div>
              </div>

              <div className="panel" role="tabpanel" id="p-sarkari" hidden aria-labelledby="t-sarkari">
                <div>
                  <h3>Taiyari mein saal nikal rahe hain. Aur kitna?</h3>
                  <ul className="fears"><li>"Is baar pakka." Pichli baar bhi yahi kaha tha.</li><li>Coaching ki fees, ghar ka kharcha, aur umar badh rahi hai.</li><li>Chhod diya toh lagega haar maan li.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>Sun &amp; 10th lord</b> · authority, government service</li><li><b>6th house</b> · competitive exams, service</li><li><b>Saturn</b> · structured, rule-based roles</li><li><b>Running dasha</b> · preparation ya attempts ke liye favourable phase</li></ul>
                </div>
                <div>
                  <div className="msg user">Taiyari jaari rakhun ya chhod doon?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>Tumhare chart mein service ke indications strong hain, aur agla Jupiter antardasha competitive efforts ke liye traditionally favourable maana jaata hai. Chart ye nahi batata ki result kya aayega. Ye batata hai ki kaunsa phase pura zor lagane ke liye better hai, aur kab backup plan pe kaam karna samajhdaari hai.</div>
                  <div style={{marginTop: '18px'}}><button className="btn" type="button" data-ask data-preset="taiyari">Mera sarkari yog dekho →</button></div>
                </div>
              </div>

              <div className="panel" role="tabpanel" id="p-business" aria-labelledby="t-business" hidden>
                <div>
                  <h3>Idea hai. Himmat bhi. Par risk kiska?</h3>
                  <ul className="fears"><li>Papa ki savings, ya shaadi ka paisa.</li><li>Fail hua toh sab kahenge "pehle hi bola tha."</li><li>Naukri chhodun, ya side mein shuru karun?</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>7th house</b> · trade, partnerships, clients</li><li><b>3rd house</b> · initiative, risk-taking</li><li><b>Mercury &amp; 11th house</b> · deal-making, gains</li><li><b>Dhana &amp; Raj yogas</b> · 1,000+ yogas checked</li></ul>
                </div>
                <div>
                  <div className="msg user">Business shuru karun ya naukri karte karte side mein try karun?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>Tumhare 3rd aur 7th house dono active hain, jo apne kaam aur clients ke saath kaam karne ki taraf indicate karta hai. Par abhi <strong>Rahu antardasha</strong> chal raha hai, jise traditionally bade commitments se pehle test karne ka phase maana jaata hai. Chart side mein chhota shuru karne aur agle phase mein scale karne ki taraf jhukta hai.</div>
                  <div style={{marginTop: '18px'}}><button className="btn" type="button" data-ask data-preset="business">Mera business yog dekho →</button></div>
                </div>
              </div>
            </div>

            <div className="timeline" aria-label="Sample dasha timeline">
              <span className="tag">Sample dasha timeline · Not just kya. Kab.</span>
              <div className="tl">
                <div><span className="now">ABHI</span><b>Saturn-Mercury</b>Build: skills, preparation</div>
                <div><b>Saturn-Ketu</b>Hold: plan, backup ready rakho</div>
                <div><b>Saturn-Venus</b>Move: change ke liye window</div>
              </div>
            </div>
          </div>
        </section>

        <section className="rule" aria-labelledby="depth">
          <div className="wrap">
            <p className="eyebrow">Why this read is different</p>
            <h2 id="depth">Teen jagah kundli dikhayi. <em>Teeno ne alag baat boli.</em></h2>
            <div className="depth">
              <div className="typ">
                <h3>Typical quick reading</h3>
                <ul><li>Sirf 10th house ka sign</li><li>Ek planet dekh ke faisla</li><li>Agle astrologer se alag jawaab</li><li>Aur saath mein ratan ki salah</li></ul>
              </div>
              <div className="aura">
                <h3>Aura ek saath kya padhta hai</h3>
                <ul>
                  <li>10th house &amp; 10th lord<span>Field aur standing</span></li>
                  <li>D10 (Dashamsha)<span>Career-specific chart</span></li>
                  <li>6th, 3rd &amp; 7th houses<span>Service vs business indications</span></li>
                  <li>Amatya Karaka<span>Tumhari working strengths</span></li>
                  <li>Running dasha<span>Abhi kaunsa phase hai</span></li>
                  <li>Saturn &amp; Jupiter transits<span>Window kab khulti hai</span></li>
                  <li>1,000+ yogas<span>Raj yoga, Dhana yoga aur aur bhi</span></li>
                  <li>Same logic every time<span>Do baar poochho, ek jawaab</span></li>
                </ul>
              </div>
            </div>
            <div className="stats">
              <div><b>10M+</b><span>real charts read</span></div>
              <div><b>10K+</b><span>Vedic rules</span></div>
              <div><b>24x7</b><span>instant answers</span></div>
              <div><b>Swiss</b><span>Ephemeris astronomy data</span></div>
            </div>
            <div className="ps" role="table" aria-label="Problem and solution">
              <div className="ps-row ps-h" role="row"><span role="columnheader">Problem</span><span role="columnheader">Aura ka solution</span></div>
              <div className="ps-row" role="row"><p role="cell">Rishtedaar, dost, YouTube: sab alag salah dete hain.</p><p role="cell"><b>Same chart, same logic, har baar.</b> Aaj poochho ya agle mahine, jawaab nahi badlega.</p></div>
              <div className="ps-row" role="row"><p role="cell">Astrologer ne sirf 10th house dekha, aur ratan bechne lage.</p><p role="cell"><b>100+ placements ek saath.</b> Na ratan, na puja package. <span className="flag" style={{margin: '0 0 0 6px'}}>Verify</span></p></div>
              <div className="ps-row" role="row"><p role="cell">"Kya karun" sab batate hain. "Kab" koi nahi batata.</p><p role="cell"><b>Dasha timeline:</b> move, build ya hold, aur agla window kab khulta hai.</p></div>
              <div className="ps-row" role="row"><p role="cell">"Sab kharab hone wala hai" sun ke aur darr lagta hai.</p><p role="cell"><b>Working dikhata hai:</b> kaunsa house, planet, period. Faisla tumhara.</p></div>
              <div className="ps-row" role="row"><p role="cell">Ghar pe ya office mein bol nahi sakte.</p><p role="cell"><b>Private, encrypted, 24/7.</b> Raat ke 2 baje bhi poochho.</p></div>
            </div>
          </div>
        </section>


                {/* Report preview and the four steps share one section. Apart they were
            96 and 47 words each, two near-empty screens in a row; together they
            answer the same question, which is what you get and how fast. */}
        <section className="rule" aria-labelledby="report">
          <div className="wrap">
            <p className="eyebrow">What you get</p>
            <h2 id="report">Poori picture, <em>faisla lene se pehle.</em></h2>

            <div className="crr-duo">
              <div className="report">
                <div className="report-head">
                  <b>Career Path Report</b>
                  <span>Sample preview</span>
                </div>
                <ol>
                  <li><b>Sarkari, private ya business</b><span>Chart kis raaste ki taraf jhukta hai, aur kyun</span></li>
                  <li><b>Abhi ka phase</b><span>Move, build ya hold, running dasha se</span></li>
                  <li className="blur" aria-hidden="true"><b>Favourable windows</b><span>Attempts, switch ya launch ke liye</span></li>
                  <li className="blur" aria-hidden="true"><b>D10 strengths</b><span>Kis tarah ka kaam suit karta hai</span></li>
                </ol>
                <div className="report-lock">
                  <p>Andar: windows, D10 strengths aur follow-up sawaal.</p>
                  <button className="btn ghost" type="button" data-report>Kya milega, dekho &rarr;</button>
                </div>
              </div>

              <div className="crr-steps">
                <p className="looks">60 second mein pehla jawaab</p>
                <ol className="steps">
                  <li><b>Birth details</b><span>Date, time, jagah</span></li>
                  <li><b>Kundli banti hai</b><span>Swiss Ephemeris data se</span></li>
                  <li><b>100+ placements</b><span>Houses, D10, dashas, yogas</span></li>
                  <li><b>Seedha jawaab</b><span>Phir follow-up poochho</span></li>
                </ol>
                <button className="btn" type="button" data-ask>Apna sawaal poochho, FREE &rarr;</button>
              </div>
            </div>
          </div>
        </section>









        {/* The page converts in Hinglish and ranks in English. These are the
            terms it is indexed for, and they bring us ~145 clicks a quarter
            with no page behind them: "career astrology", "career prediction by
            date of birth", "career horoscope". Forcing them into the hero would
            cost more in conversion than it gains in rank, so they live here. */}
        <section className="rule crr-seo" aria-labelledby="seo">
          <div className="wrap">
            <p className="eyebrow">Career astrology</p>
            <h2 id="seo">What your chart actually says about work</h2>

            <div className="crr-seo__grid">
              <article>
                <h3>Career prediction by date of birth</h3>
                <p>
                  A career reading needs the date, the time and the place, not
                  the date alone. Those three fix the lagna, the house cusps and
                  the dasha sequence, and the houses are what turn a chart into
                  a career reading. With only a date you get the planetary signs
                  and little else.
                </p>
              </article>

              <article>
                <h3>The houses that describe work</h3>
                <p>
                  The tenth house and its lord describe the field and the
                  standing you reach in it. The Dashamsha, or D10, is the
                  divisional chart drawn specifically for career. The sixth
                  covers service, competition and examinations, which is why it
                  matters so much for a government job question, and the third
                  and seventh cover initiative, trade and clients.
                </p>
              </article>

              <article>
                <h3>Why timing beats placement</h3>
                <p>
                  A strong tenth house that no current period is activating
                  stays quiet, and that is the most common reason a career
                  reading feels wrong. The Vimshottari dasha says which graha is
                  running your life now, and the transits of Saturn and Jupiter
                  mark when a change is easier to make.
                </p>
              </article>

              <article>
                <h3>What it cannot tell you</h3>
                <p>
                  The arithmetic is deterministic: given correct birth details,
                  the longitudes, cusps and dasha dates are a calculation. What
                  no chart can do is name your employer, guarantee an exam
                  result or promise a profit. Those depend on preparation,
                  cut-offs, capital and execution. Anyone claiming otherwise is
                  selling something.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* PRICING. Three real destinations, no placeholders:
              free question -> the chat flow, report -> the Career & Success
              Blueprint unlock on /reports, membership -> SubscribeSheet.
              The report price is read live from PricingContext, so it tracks
              currency and any member rate instead of being hardcoded. */}
        <section className="rule" id="plans" aria-labelledby="offer">
          <div className="wrap">
            <p className="eyebrow">Pricing</p>
            <h2 id="offer">Free se shuru karo. <em>Faisle se pehle gehrai mein jao.</em></h2>

            <div className="plans">
              <div className="plan">
                <h3>Free sawaal</h3>
                <p className="price">{formatPrice(0)}</p>
                <ul>
                  <li>1 career sawaal, tumhare chart se</li>
                  <li>Tumhari kundli</li>
                  <li>Koi card nahi</li>
                </ul>
                <button className="btn ghost" type="button" data-ask>
                  Free Poochho
                </button>
              </div>

              <div className="plan star">
                <h3>Career Path Report</h3>
                <p className="price">
                  {reportPrice ? formatPrice(reportPrice) : "\u2014"}
                </p>
                <ul>
                  <li>Sarkari / private / business indications</li>
                  <li>Abhi ka phase: move, build ya hold</li>
                  <li>Favourable windows</li>
                  <li>10th house aur D10 analysis</li>
                  <li>Report pe follow-up sawaal</li>
                </ul>
                <button className="btn" type="button" data-report>
                  Meri Report Chahiye
                </button>
              </div>

              <div className="plan">
                <h3>Aura Plus</h3>
                <p className="price">
                  Monthly <small>membership</small>
                </p>
                <ul>
                  <li>Unlimited follow-up sawaal</li>
                  <li>Har offer, har attempt pe poochho, 24/7</li>
                  <li>Member rates on every report</li>
                </ul>
                <button className="btn ghost" type="button" data-subscribe>
                  Membership Lo
                </button>
              </div>
            </div>

            <p className="pay">UPI, cards, wallets. Turant access.</p>
          </div>
        </section>

        

        <section className="rule" aria-labelledby="faq">
          <div className="wrap">
            <p className="eyebrow">Questions</p>
            <h2 id="faq">Poochne se pehle.</h2>
            <div className="faq">
              <details><summary>Kya ye batayega mera exam clear hoga?</summary><p>Nahi. Exam result tumhari taiyari, cut-off aur exam process pe depend karta hai. Aura batata hai ki tumhare chart mein service ke indications kitne strong hain, aur kaunsa phase pura zor lagane ke liye traditionally favourable hai.</p></details>
              <details><summary>Business mein profit hoga ya nahi, ye batayega?</summary><p>Nahi. Koi bhi paise ya profit ki guarantee nahi de sakta. Aura dikhata hai ki tumhara chart business ya partnership ki taraf kitna indicate karta hai, aur kaunsa phase shuru karne ke liye better hai.</p></details>
              <details><summary>Kya ye sach mein accurate hai?</summary><p>Kundli Swiss Ephemeris astronomy data se banti hai, aur Aura har insight ke saath batata hai ki wo kis house, planet aur period se aayi. Ye phases aur favourable windows dikhata hai, fixed dates nahi.</p></details>
              <details><summary>Birth time nahi pata?</summary><p>Chalega. Approximate time se bhi chart ban jaata hai aur zyada tar cheezein padhi ja sakti hain. Exact time hone se lagna aur dasha timing sharper hoti hai, isliye jitna paas ho sake utna accurate time daalo.</p></details>
              <details><summary>Free sawaal ke baad kya?</summary><p>Tumhe poora jawaab milta hai. Agar sarkari/private/business ki poori picture, favourable windows aur D10 strengths chahiye, toh Career Path Report mein milega.</p></details>
              <details><summary>What is career astrology?</summary><p>Career astrology reads the part of your birth chart that describes work: the 10th house and its lord, the D10 or Dashamsha divisional chart, the 6th house for service and competition, and the 3rd and 7th for your own ventures. Read together with the dasha period you are running, it describes the kind of work your chart leans toward and which phase you are in, rather than naming a job title.</p></details>
              <details><summary>Can astrology predict my career by date of birth?</summary><p>A career prediction by date of birth needs the date, the time and the place, not the date alone. Those three fix the lagna, the house cusps and the dasha sequence, which is where the career reading comes from. With only a date you get the planetary signs but not the houses, and the houses are what make it a career reading.</p></details>
              <details><summary>App download karna padega?</summary><p>Nahi. Yahin browser mein chalta hai. <a href="https://play.google.com/store/apps/details?id=com.astroaura.auraai" rel="noopener" target="_blank">Android app</a> bhi hai.</p></details>
            </div>
          </div>
        </section>





        {/* Closing CTA. The app's standard landing-page card (FooterEmailBanner)
            in format, but carrying this page's own Hinglish copy instead of its
            generic English line, and its own microcopy row. Rebuilt here rather
            than reused because that component is shared across /market, /global
            and the SEO landings and takes no copy props. */}
        <section className="crr-close" aria-labelledby="close">
          <div className="crr-close__card">
            <h2 id="close" className="crr-close__title">
              Switch, wait ya risk? <em>Log kya kahenge baad mein. Pehle apna chart dekho.</em>
            </h2>
            <p className="crr-close__sub">
              Pehla sawaal bilkul free. Apne chart se, 60 second mein.
            </p>
            <button className="crr-close__cta" type="button" data-ask>
              Apna Sawaal Poochho, FREE
            </button>
            <p className="crr-close__note">
              Pehla sawaal free <span aria-hidden="true">&middot;</span> Koi card nahi{" "}
              <span aria-hidden="true">&middot;</span> Private &amp; encrypted
            </p>
          </div>
        </section>
      </main>

      {/* The site footer is suppressed on this route, so Privacy and Terms
          still need a home: Google Ads expects a landing page to surface them. */}
      <footer className="crr-foot">
        <div className="wrap">
          <span>&copy; Aura AI</span>
          <span>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-and-conditions">Terms</Link>
          </span>
        </div>
      </footer>


      <SubscribeSheet
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        source="career_page"
      />

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </div>
  );
}
